import {Trans} from '@lingui/macro';
import {isString, omit, pickBy} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Parameter} from '../../../editors/commons/parameterModel';
import {DateWithTimezoneParam} from '../../../editors/commons/paramModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {DateIsCondition} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {InputStringParameter} from '../inputs/inputStringParameter';
import {TimezoneModel, TimezoneParameter} from '../parameters/timezoneParameter';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Constants.
 */

const format = 'yyyy-LL-dd';

/*
 * Types.
 */

interface RuleDateIsConditionCardProps {
  condition: DateIsCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  onUpdate: HandlerOf<DateIsCondition>;
  onDelete?: HandlerOf<DateIsCondition>;
}

/*
 * Styles.
 */

const StyledLabel = styled.div`
  padding: 8px 5px;
`;

/*
 * Component.
 */

export const RuleDateIsConditionCard: FC<RuleDateIsConditionCardProps> = (props) => {
  const {condition, conditionDefinition, errorMessage, onUpdate, onDelete, isDisabled} = props;

  const {params} = condition;
  const {comparator, date, end_date: endDate} = params;

  // todo: update when we have a proper date picker that manages the timezone for each parameter
  const timezone = date.timezone || DateTime.local().zoneName;
  const isoString = date.date ?? DateTime.now().toJSDate().toISOString();
  const [readableDate, setReadableDate] = useState(toReadableDate({date: isoString, timezone}));
  const endIsoString = endDate?.date ?? DateTime.now().plus({days: 1}).toJSDate().toISOString();
  const [readableEndDate, setReadableEndDate] = useState(toReadableDate({date: endIsoString, timezone}));

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  const maybeFormatError = (!DateTime.fromFormat(readableDate, format).isValid ||
    (comparator === 'between' && !DateTime.fromFormat(readableEndDate, format).isValid)) && (
    <Trans id="rule-date-is-card-invalid-format">Format must be YYYY-MM-DD</Trans>
  );

  const renderDateParameter = () => {
    const updateReadableDate = (d: string) => {
      const roundedDates = roundDates(comparator, d, readableEndDate, timezone);

      if (roundedDates) {
        const updatedParams = {...params, ...roundedDates};
        if (comparator === 'between') {
          onUpdate({...condition, params: updatedParams});
        } else {
          onUpdate({...condition, params: omit(updatedParams, ['end_date'])});
        }
        setReadableDate(toReadableDate(roundedDates.date));
      } else {
        setReadableDate(d);
      }
    };

    return (
      <InputStringParameter
        stringParameter={{id: 'rule-date-is-card-date-parameter', type: 'string'}}
        value={readableDate}
        onValueChange={updateReadableDate}
      />
    );
  };

  const publicParameters = pickBy(conditionDefinition.parameters, Parameter.isPublic);

  const onUpdateComparator = (dateIsCondition: DateIsCondition) => {
    const {params: conditionParams} = dateIsCondition;
    const roundedDates = roundDates(conditionParams.comparator, readableDate, readableEndDate, timezone);
    const updatedParams = roundedDates ? {...conditionParams, ...roundedDates} : conditionParams;

    if (conditionParams.comparator === 'between') {
      onUpdate({...dateIsCondition, params: updatedParams});
    } else {
      onUpdate({...dateIsCondition, params: omit(updatedParams, ['end_date'])});
    }
  };

  const {
    parameters: [comparatorParameter],
  } = renderGenericRuleParameters(
    condition,
    conditionDefinition,
    RuleDefaultCardTypesEnum.CONDITION,
    onUpdateComparator,
    publicParameters,
    isDisabled,
  );

  const maybeRenderEndDateParameter = () => {
    if (comparator !== 'between') {
      return undefined;
    }

    const updateReadableEndDate = (d: string) => {
      const roundedDates = roundDates(comparator, readableDate, d, timezone);

      if (roundedDates) {
        onUpdate({...condition, params: {...params, ...roundedDates}});
        setReadableEndDate(toReadableDate(roundedDates.end_date));
      } else {
        setReadableEndDate(d);
      }
    };

    return (
      <>
        <StyledLabel>
          <Trans id="rule-date-is-card-between-and">and</Trans>
        </StyledLabel>
        <InputStringParameter
          stringParameter={{id: 'rule-date-is-card-end-date-parameter', type: 'string'}}
          value={readableEndDate}
          onValueChange={updateReadableEndDate}
        />
      </>
    );
  };

  const onCancel = onDelete && (() => onDelete(condition));

  const onTimezoneChange = (tz: OrNull<SelectedDropdownItem<TimezoneModel>>) => {
    if (!isString(tz)) {
      return;
    }

    const roundedDates = roundDates(comparator, readableDate, readableEndDate, tz);
    if (!roundedDates) {
      return;
    }

    const updatedParams = {comparator, ...roundedDates};

    if (comparator === 'between') {
      onUpdate({...condition, params: updatedParams});
    } else {
      onUpdate({...condition, params: omit(updatedParams, ['end_date'])});
    }
  };

  const timezoneParameter = (
    <TimezoneParameter
      key="rule-date-is-card-timezone-parameter"
      selectedItem={timezone}
      onChange={onTimezoneChange}
      isDisabled={isDisabled || Boolean(maybeFormatError)}
    />
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-date-is-card-title">Date</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      {comparatorParameter}
    </>
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<RuleConditionIcon id={condition.type} />}
      collapsableParameters={[timezoneParameter]}
      errorMessage={maybeFormatError || errorMessage}
      onCancel={onCancel}
    >
      {renderDateParameter()}
      {maybeRenderEndDateParameter()}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function toReadableDate(dateWithTimezone: DateWithTimezoneParam) {
  return DateTime.fromISO(dateWithTimezone.date, {
    zone: dateWithTimezone.timezone,
  }).toFormat(format);
}

function roundDates(comparator: string, date: string, endDate: string, timezone: string) {
  const dateMoment = DateTime.fromFormat(date, format, {zone: timezone});
  // todo update when each date picker has their timezone
  const endDateMoment = DateTime.fromFormat(endDate, format, {zone: timezone});

  if (!dateMoment.isValid || !endDateMoment.isValid) {
    return undefined;
  }

  const roundedDate = comparator === 'before' ? dateMoment.endOf('day') : dateMoment.startOf('day');
  const roundedEndDate = endDateMoment.endOf('day');

  return {
    date: {date: roundedDate.toJSDate().toISOString(), timezone},
    end_date: {date: roundedEndDate.toJSDate().toISOString(), timezone},
  };
}
